import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { lazy, Suspense, type JSX } from "react";
import Layout from "./components/layout/Layout";
import ScrollToTop from "./components/layout/ScrollToTop"; // ✅ Import this
import PageAccessGuard from "./components/layout/PageAccessGuard";
import Loader from "./components/common/Loader"; // Use lightweight loader for route transitions

const LandingPage = lazy(() => import("./pages/home/LandingPage"));
const AboutUs = lazy(() => import("./pages/about/AboutUsPage"));
const KarmatekPage = lazy(() => import("./pages/karmatek/KarmatekPage"));
const KarmatekEventDetails = lazy(
  () => import("./pages/karmatek/KarmatekEventDetails"),
);
const KarmatekLeaderboardPage = lazy(
  () => import("./pages/karmatek/KarmatekLeaderboardPage"),
);
const KarmatekSchedulePage = lazy(
  () => import("./pages/karmatek/KarmatekSchedulePage"),
);
const EventsPage = lazy(() => import("./pages/events/EventsPage"));
const GalleryPage = lazy(() => import("./pages/gallery/GalleryPage"));
const FAQ = lazy(() => import("./pages/faq/FAQPage"));
const ContactPage = lazy(() => import("./pages/contact/ContactPage"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute"));
const CpContestPage = lazy(() => import("./pages/cp-contest/CpContestPage"));
const TreasureHuntPage = lazy(
  () => import("./pages/karmatek/TreasureHuntPage"),
);
const TreasureHuntLeaderboard = lazy(
  () => import("./pages/karmatek/TreasureHuntLeaderboard"),
);

// Lightweight loading fallback to avoid flash of unstyled content
const RouteLoader = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#000",
    }}
  >
    <Loader />
  </div>
);

const AppRouter = (): JSX.Element => {
  return (
    <Router>
      <ScrollToTop /> {/* ✅ Add this line */}
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<LandingPage />} />
            <Route
              path="/about-us"
              element={
                <PageAccessGuard pageKey="about">
                  <AboutUs />
                </PageAccessGuard>
              }
            />
            <Route
              path="/karmatek"
              element={
                <PageAccessGuard pageKey="karmatek">
                  <KarmatekPage />
                </PageAccessGuard>
              }
            />
            <Route
              path="/karmatek/leaderboard"
              element={
                <PageAccessGuard pageKey="karmatek">
                  <KarmatekLeaderboardPage />
                </PageAccessGuard>
              }
            />
            <Route
              path="/karmatek/schedule"
              element={
                <PageAccessGuard pageKey="karmatek">
                  <KarmatekSchedulePage />
                </PageAccessGuard>
              }
            />
            <Route
              path="/karmatek/event/:eventId"
              element={
                <PageAccessGuard pageKey="karmatek">
                  <KarmatekEventDetails />
                </PageAccessGuard>
              }
            />
            <Route
              path="/karmatek/treasure-hunt"
              element={
                <PageAccessGuard pageKey="karmatek">
                  <TreasureHuntPage />
                </PageAccessGuard>
              }
            />
            <Route
              path="/karmatek/treasure-hunt/leaderboard"
              element={
                <PageAccessGuard pageKey="karmatek">
                  <TreasureHuntLeaderboard />
                </PageAccessGuard>
              }
            />
            <Route
              path="/events"
              element={
                <PageAccessGuard pageKey="events">
                  <EventsPage />
                </PageAccessGuard>
              }
            />
            <Route
              path="/gallery"
              element={
                <PageAccessGuard pageKey="gallery">
                  <GalleryPage />
                </PageAccessGuard>
              }
            />
            <Route
              path="/contact"
              element={
                <PageAccessGuard pageKey="contact">
                  <ContactPage />
                </PageAccessGuard>
              }
            />
            <Route
              path="/faq"
              element={
                <PageAccessGuard pageKey="faq">
                  <FAQ />
                </PageAccessGuard>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/cp-contest/:contestId"
              element={
                <PageAccessGuard pageKey="events">
                  <CpContestPage />
                </PageAccessGuard>
              }
            />
            <Route
              path="*"
              element={
                <div className="text-danger text-center py-5">
                  404 Not Found
                </div>
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRouter;
